import React, { useState, useEffect } from 'react';

import { Input, Button, message, Modal, Checkbox, Tooltip } from 'antd';
import { Formik, ErrorMessage } from 'formik';
import { useHistory } from 'react-router-dom';
import { T } from 'react-translator-component';
import styled from 'styled-components';

import TagsTransfer from './TagsTransfer';
import { confirmTrustedUser } from './confirmTrustedUser';
import { createUser, getDocTypes } from '../../../hooks/users';
import { cpfMask } from '../../../utils/masks';
import { getProName } from '../../../utils/getProName/getProName';
import OperationsTransfer from '../OperationsTransfer';
import CompanySelect from '../../molecules/CompanySelect';
import { QuestionIcon } from '../../atoms/icons/QuestionIcon';
import { COLORS } from '../../../utils/theme/colors';
import { hideTrustedUserButton } from '../../../utils/hideTrustedUserButton/hideTrustedUserButton';
import {
  INVALID_EMAIL_MESSAGE,
  isValidEmail,
  normalizeEmail,
} from '../../../utils/email/email';

const UserCreateFormWrapper = styled.div`
  form {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;

    label:first-child {
      margin-right: 10px;
    }

    button {
      margin-top: 20px;
    }
  }
`;

const FormLabel = styled.label`
  font-size: 14px;
  font-weight: bold;
`;

const FormLabelInput = styled.input`
  font-size: 14px;
  font-weight: bold;
`;

const LabelInputWrapper = styled.div`
  display: inline;
`;

const FormInputWrapper = styled.div`
  width: 100%;
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  align-items: start;
  justify-content: flex-start;

  select {
    padding: 4px 20px 4px 8px;
    border: 1px solid #d9d9d9;
    border-radius: 2px;
    touch-action: manipulation;
    appearance: none;
    background-image: url("data:image/svg+xml,%3C%3Fxml version='1.0' standalone='no'%3F%3E%3C!DOCTYPE svg PUBLIC '-//W3C//DTD SVG 1.1//EN' 'http://www.w3.org/Graphics/SVG/1.1/DTD/svg11.dtd'%3E%3Csvg t='1597601650772' class='icon' viewBox='0 0 1024 1024' version='1.1' xmlns='http://www.w3.org/2000/svg' p-id='1210' xmlns:xlink='http://www.w3.org/1999/xlink' width='16' height='16'%3E%3Cdefs%3E%3Cstyle type='text/css'%3E%3C/style%3E%3C/defs%3E%3Cpath d='M726.652801 429.305603 297.347199 429.305603 512.193405 638.156258Z' p-id='1211'%3E%3C/path%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right;

    &:focus {
      border-color: #40a9ff;
      border-right-width: 1px !important;
      outline: 0;
      -webkit-box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
      box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
    }

    &:hover {
      border-color: #40a9ff;
      border-right-width: 1px !important;
    }
  }

  .docType {
    padding: 0px 20px 0px 8px;
  }

  @media (min-width: 1024px) {
    input:not([type='checkbox']) {
      min-width: 300px;
    }
  }

  input:hover,
  input:focus,
  input:active,
  input:disabled:hover,
  .ant-input-affix-wrapper:hover,
  .ant-input-affix-wrapper:focus,
  .ant-input-affix-wrapper:active,
  .ant-input-affix-wrapper:visited,
  .ant-input-affix-wrapper-focused,
  select:hover,
  select:focus,
  select:active,
  textarea:hover,
  textarea:focus,
  textarea:active {
    border-right-width: 0 !important;
    border-color: #40a9ff;
    outline: none;
    box-shadow: none;
  }

  input:disabled:hover {
    border-color: rgba(0, 0, 0, 0.3);
  }

  .ant-input-affix-wrapper {
    border-right-width: 0 !important;
  }

  input,
  .ant-input-affix-wrapper,
  select,
  textarea {
    font-size: 14px;
    font-weight: normal;
    border-radius: 0 0 1px 0;
    border-width: 0 0 1px 0;
    border-color: #000;

    /* &:hover,
    &:focus,
    &:disabled:hover {
      border-right-width: 0 !important;
      box-shadow: none;
    } */
  }

  input[type='number']::-webkit-outer-spin-button,
  input[type='number']::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

const FormInputGroupWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: center;
  width: 100%;

  > div:first-child {
    margin-right: 40px;
  }
`;

const MessageBox = styled.div`
  color: red;
  font-size: 12px;
`;

const PasswordValidationList = styled.ul`
  margin-top: 8px;
  margin-bottom: 0;
  padding-left: 0;
`;

const PasswordValidationItem = styled.li`
  color: ${({ isValid }) => (isValid ? 'green' : 'red')};
  font-size: 14px;
  list-style: none;
`;

const InputField = styled.input`
  width: 342px;
`;

const CheckBoxDiv = styled.div`
  margin-top: 20px;
  flex-direction: column;
`;

const TooltipPositionWrapper = styled.div`
  position: relative;
  display: inline-block;
`;

const StyledQuestionIconWrapper = styled.span`
  position: absolute;
  top: -4px;
  right: 8px;
  transform: translateY(-50%);
  cursor: pointer;
`;

const UserCreateForm = ({
  initialValues,
  type,
  tenants,
  userGroups,
  availableOcppTags,
  isVehicle,
  companiesList,
  getCompaniesData,
  chargePointsList,
}) => {
  const history = useHistory();

  const [docTypeList, setDocTypeList] = useState({});
  const [targetKeys, setTargetKeys] = useState([]);
  const [chargeBoxKeys, setChargeBoxKeys] = useState([]);
  const [cpf, setCpf] = useState('');

  useEffect(() => {
    (async function () {
      const docTypesData = await getDocTypes();
      setDocTypeList(docTypesData);
      if (docTypesData?.docTypeList?.length) {
        initialValues.docType = docTypesData.docTypeList[0];
      }
    })();
  }, []);

  const allowedTenantPks = (tenants?.list || []).map((t) => Number(t.tenant_pk));
  
  const currentUserGroupPk = Number(initialValues?.userGroupPk);

  const visibleUserGroups =
      allowedTenantPks.length > 0
        ? (userGroups?.userGroupList || []).filter(
            (group) =>
              allowedTenantPks.includes(Number(group.tenantCreatorPk)) ||
              Number(group.userGroupPk) === currentUserGroupPk,
          )
        : userGroups?.userGroupList || [];

  return (
    <Formik
      initialValues={initialValues}
      validate={(values) => {
        const errors = {};
        if (!values.firstName)
          errors.firstName = isVehicle
            ? T('The license plate field is mandatory.')
            : T('The Name field is mandatory.');
        if (!values.password && !isVehicle)
          errors.password = T('The Password field is mandatory.');
        if (!isVehicle && !normalizeEmail(values.email))
          errors.email = T('The E-mail field is mandatory.');
        else if (!isVehicle && !isValidEmail(values.email))
          errors.email = T(INVALID_EMAIL_MESSAGE);
        if (!values.phone && !isVehicle) {
          const [tenant] = tenants.list.filter(
            (tenant) => tenant.tenant_pk === Number(values.tenant_pk),
          );

          if (tenant.sms_enabled) {
            errors.phone = T('The Phone field is mandatory.');
          }
        }

        if (!isVehicle && !/^[a-zA-Z\u00C0-\u00ff ]+$/.test(values.name))
          errors.name = T('The Name field must only contain letters.');

        if (isVehicle && (!values.company || !values.company.uuid)) {
          errors.company = T('The Company field is mandatory.');
        }

        return errors;
      }}
      onSubmit={(values, { setSubmitting }) => {
        setTimeout(async () => {
          let data = null;
          if (values.docNumber == null || values.docNumber == '') {
            values.docType = null;
          } else if (values.docType == 'CPF') {
            values.docNumber = values.docNumber.replace(/\D/g, '');
          }
          if (targetKeys?.length) {
            values.ocppTags = targetKeys;
          }
          if (chargeBoxKeys?.length) {
            values.chargeBoxes = chargeBoxKeys;
          }

          if (type === 'create') {
            data = await createUser({
              ...values,
              company_uuid: values?.company?.uuid || null,
              email: normalizeEmail(values.email) || null,
            });
          }

          if (data !== null) {
            if (data.type === 'success') {
              if (type === 'create') {
                message.success('Usuário criado com sucesso!');
              } else {
                message.success('Usuário alterado com sucesso!');
              }

              if (isVehicle) {
                history.push('/users');

                return;
              }

              setTimeout(() => {
                history.goBack();
              }, 1000);
            } else {
              if (type === 'create') {
                if (data.status === 403) {
                  Modal.error({
                    title: 'Limite de usuários atingido!',
                    content: (
                      <div style={{ fontWeight: 300 }}>
                        Caso deseje adicionar mais usuários, será necessário
                        fazer upgrade para a versão{' '}
                        <strong style={{ fontWeight: 700 }}>
                          {getProName()}
                        </strong>{' '}
                        ou excluir algum usuário.
                      </div>
                    ),
                  });
                } else {
                  message.error(
                    'Não foi possível realizar a operação. Certifique-se de que os campos foram corretamente preenchidos.',
                  );
                }
              } else {
                message.error(
                  'Não foi possível realizar a operação. Certifique-se de que os campos foram corretamente preenchidos.',
                );
              }
            }
          } else {
            message.error(
              'Não foi possível realizar a operação. Certifique-se de que os campos foram corretamente preenchidos.',
            );
          }
          setSubmitting(false);
        }, 400);
      }}
    >
      {({
        values,
        handleChange,
        handleBlur,
        handleSubmit,
        setFieldValue,
        isSubmitting,
        errors,
      }) => {
        const handleCpfChange = (e) => {
          setCpf(cpfMask(e.target.value));
        };

        const password = values.password || '';
        const passwordValidations = {
          length: password.length >= 8 && password.length <= 25,
          uppercase: /[A-Z]/.test(password),
          lowercase: /[a-z]/.test(password),
          special: /[!@#$%^&*()_\-+=<>?/\\|{}~:;.,'"€£¥₩÷]|\[|\]/.test(password),
          number: /\d/.test(password),
        };
        const isPasswordValid =
          passwordValidations.length &&
          passwordValidations.uppercase &&
          passwordValidations.lowercase &&
          passwordValidations.special &&
          passwordValidations.number;

        return (
          <UserCreateFormWrapper>
            <form onSubmit={handleSubmit}>
              <FormInputGroupWrapper>
                <FormInputWrapper>
                  <FormLabel>{isVehicle ? T('Placa') : T('Name')}:</FormLabel>
                  <Input
                    name='firstName'
                    label='firstName'
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.firstName}
                    required
                    maxLength={20}
                    allowClear
                  />
                  <ErrorMessage name='firstName' component={MessageBox} />
                </FormInputWrapper>
                {!isVehicle && (
                  <FormInputWrapper>
                    <FormLabel>{T('E-mail')}:</FormLabel>
                    <Input
                      name='email'
                      label='email'
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.email}
                      required
                      maxLength={55}
                      allowClear
                    />
                    <ErrorMessage name='email' component={MessageBox} />
                  </FormInputWrapper>
                )}
              </FormInputGroupWrapper>

              {!isVehicle && (
                <FormInputGroupWrapper>
                  <FormInputWrapper>
                    <FormLabel>{T('Password')}:</FormLabel>
                    <Input
                      type='password'
                      name='password'
                      label='password'
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.password}
                      required
                      maxLength={25}
                      allowClear
                    />
                    <ErrorMessage name='password' component={MessageBox} />
                    <PasswordValidationList>
                      <PasswordValidationItem isValid={passwordValidations.length}>
                        {passwordValidations.length ? '✔' : '❌'}{' '}
                        {T('8 to 25 characters')}
                      </PasswordValidationItem>
                      <PasswordValidationItem
                        isValid={passwordValidations.uppercase}
                      >
                        {passwordValidations.uppercase ? '✔' : '❌'}{' '}
                        {T('At least one uppercase letter')}
                      </PasswordValidationItem>
                      <PasswordValidationItem
                        isValid={passwordValidations.lowercase}
                      >
                        {passwordValidations.lowercase ? '✔' : '❌'}{' '}
                        {T('At least one lowercase letter')}
                      </PasswordValidationItem>
                      <PasswordValidationItem
                        isValid={passwordValidations.special}
                      >
                        {passwordValidations.special ? '✔' : '❌'}{' '}
                        {T('At least one special character')}
                      </PasswordValidationItem>
                      <PasswordValidationItem isValid={passwordValidations.number}>
                        {passwordValidations.number ? '✔' : '❌'}{' '}
                        {T('At least one number')}
                      </PasswordValidationItem>
                    </PasswordValidationList>
                  </FormInputWrapper>

                  {docTypeList.docTypeList?.length ? (
                    <FormInputWrapper>
                      <LabelInputWrapper>
                        <FormLabelInput
                          className='docType'
                          as='select'
                          name='docType'
                          label='docType'
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.docType}
                        >
                          {docTypeList?.docTypeList?.length &&
                            docTypeList?.docTypeList?.map((docType) => (
                              <option value={docType}>{docType}</option>
                            ))}
                        </FormLabelInput>
                        <FormLabel>:</FormLabel>
                      </LabelInputWrapper>
                      {values.docType == 'CPF' ? (
                        <Input
                          name='docNumber'
                          label='docNumber'
                          onChange={(x) => {
                            handleCpfChange(x);
                          }}
                          onBlur={handleBlur}
                          maxLength='14'
                          value={(values.docNumber = cpf)}
                        />
                      ) : (
                        <Input
                          name='docNumber'
                          label='docNumber'
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.docNumber}
                        />
                      )}
                      <ErrorMessage name='docNumber' component={MessageBox} />
                    </FormInputWrapper>
                  ) : (
                    <FormInputWrapper />
                  )}
                </FormInputGroupWrapper>
              )}

              <FormInputGroupWrapper>
                {isVehicle && (
                  <FormInputWrapper>
                    <FormLabel className='userGroupLabel'>
                      {T('Company')}:
                    </FormLabel>

                    <CompanySelect
                      history={history}
                      values={values}
                      onOptionClick={(item) => setFieldValue('company', item)}
                      onChange={(value) => setFieldValue('company', value)}
                      companiesList={companiesList}
                      getCompaniesData={getCompaniesData}
                    />

                    {errors.company && (
                      <MessageBox>{errors.company}</MessageBox>
                    )}
                  </FormInputWrapper>
                )}

                <FormInputWrapper>
                  <FormLabel>{T('Account')}:</FormLabel>
                  <InputField
                    type='number'
                    as='select'
                    name='tenant_pk'
                    label='tenant_pk'
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.tenant_pk}
                    required
                    maxLength={20}
                    allowClear
                  >
                    {tenants?.list?.length &&
                      tenants?.list?.map(({ tenant_pk, name }) => (
                        <option value={tenant_pk}>
                          {tenant_pk} - {name}
                        </option>
                      ))}
                  </InputField>
                  <ErrorMessage name='tenant_pk' component={MessageBox} />
                </FormInputWrapper>

                {!isVehicle && (
                  <FormInputWrapper>
                    <FormLabel className='userGroupLabel'>
                      {T('User Group')}:
                    </FormLabel>
                    <InputField
                      className='userGroupInputField'
                      type='number'
                      as='select'
                      name='user_group_pk'
                      label='user_group_pk'
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.user_group_pk}
                      required
                      maxLength={20}
                      allowClear
                    >
                      {visibleUserGroups?.length > 0 &&
                        visibleUserGroups.map(({ userGroupPk, name }) => (
                          <option value={userGroupPk}>
                            {userGroupPk} - {name}
                          </option>
                        ))}
                    </InputField>
                    <ErrorMessage name='user_group_pk' component={MessageBox} />
                  </FormInputWrapper>
                )}
              </FormInputGroupWrapper>

              <FormInputGroupWrapper style={{ justifyContent: 'initial' }}>
                {!isVehicle && (
                  <FormInputWrapper style={{ width: '47%' }}>
                    <FormLabel>{T('Phone')}:</FormLabel>
                    <Input
                      type='number'
                      name='phone'
                      label='phone'
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.phone}
                      required
                      maxLength={40}
                      allowClear
                    />
                    <ErrorMessage name='phone' component={MessageBox} />
                  </FormInputWrapper>
                )}
              </FormInputGroupWrapper>

              {!hideTrustedUserButton() && !isVehicle && (
                <CheckBoxDiv>
                  <Tooltip
                    title={T(
                      'By selecting this option, the system will skip credit card authenticity verification for this user.',
                    )}
                  >
                    <TooltipPositionWrapper>
                      <StyledQuestionIconWrapper>
                        <QuestionIcon size={15} color={COLORS.primary} />
                      </StyledQuestionIconWrapper>
                      <FormLabel style={{ marginRight: '4px' }}>
                        {T('Trusted user')}:
                      </FormLabel>
                    </TooltipPositionWrapper>
                  </Tooltip>

                  <Checkbox
                    name='skip_three_ds_verification'
                    label='skip_three_ds_verification'
                    as='select'
                    onChange={(e) => {
                      if (e.target.checked) {
                        confirmTrustedUser(() =>
                          setFieldValue('skip_three_ds_verification', true),
                        );
                      } else {
                        handleChange(e);
                      }
                    }}
                    onBlur={handleBlur}
                    checked={values.skip_three_ds_verification}
                  />
                  <ErrorMessage
                    name='skip_three_ds_verification'
                    component={MessageBox}
                  />
                </CheckBoxDiv>
              )}

              <FormInputWrapper>
                <FormLabel>{T('Ocpp Tags')}:</FormLabel>
                <TagsTransfer
                  dataSource={availableOcppTags}
                  targetKeys={targetKeys}
                  setTargetKeys={setTargetKeys}
                />
              </FormInputWrapper>

              {isVehicle && (
                <FormInputWrapper>
                  <FormLabel>{T('Chargeboxes')}:</FormLabel>
                  <OperationsTransfer
                    dataSource={{
                      cpList:
                        chargePointsList.chargePointsList.map((cp) => ({
                          ...cp,
                          chargeBoxDescription: cp.description,
                        })) || [],
                    }}
                    targetKeys={chargeBoxKeys}
                    setTargetKeys={setChargeBoxKeys}
                    maxHeight={300}
                  />
                </FormInputWrapper>
              )}

              <Button
                type='primary'
                disabled={isSubmitting || (!isVehicle && !isPasswordValid)}
                onClick={handleSubmit}
              >
                {T('Submit')}
              </Button>
            </form>
          </UserCreateFormWrapper>
        );
      }}
    </Formik>
  );
};

export default UserCreateForm;
